import { ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, OnInit, signal, ViewChild } from '@angular/core';
import {
  ButtonV3Component,
  ColumnDefinitionV3,
  ColumnRendererComponentV3,
  ComplexDialogV3Service,
  TableV3Component,
  ModalV2Module,
  SnackbarV3Service,
  ProgressSpinnerComponent,
  TableServiceV3,
  TextFilterV3Component,
  SnackbarMessage,
  PageHeaderV3Component,
  PageContentLayoutV3Component,
  SearchBarV3Component,
  SelectV2Module,
  SelectOption,
} from '@gea/digital-ui-lib';
import { DataMaintenanceService } from './data-maintenance.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DividerModule } from 'primeng/divider';
import { debounceTime, finalize, from, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { saveAs } from 'file-saver';
import {
  AssetConfigResponse,
  AssetSearchResult,
  AssetSearchResponse,
  AssetTableRow,
  AssetType,
  DataMaintenanceAssetResponse,
  DataMaintenanceAsset,
  AssetCategory,
  FileUpload,
  AssetActionMenu,
  DevOpsCommitResponse,
  MenuOption,
  ResolvedMenuOption,
  DevOpsUncommitedCountResponse,
  WorktreeInfo,
  WorktreeDeleteCheckResponse,
  ValidationRules,
} from './data-maintenance.model';
import { RouteReuseStrategy } from '@angular/router';
import { NoRouteReuseStrategy } from 'src/app/shared/services/noroutereusestategy.service';
import { CommitDialogComponent } from './commit-dialog/commit-dialog.component';
import { RenameDialogComponent } from './rename-dialog/rename-dialog.component';
import { Tree } from 'primeng/tree';
import { MenuItem, TreeNode } from 'primeng/api';
import { Skeleton } from 'primeng/skeleton';
import { OptionsTableMenuComponent } from 'src/app/shared/components/options-menu/options-table-menu.component';
import { OverrideIndicatorRendererComponent } from './renderers/override-indicator-renderer.component';
import { FileNameRendererComponent } from './renderers/file-icon-renderer.component';
import { FilePreviewSidebarComponent } from './file-preview-sidebar/file-preview-sidebar.component';
import { RevertAssetDialogComponent } from './revert-asset-dialog/revert-asset-dialog.component';
import { DeleteAssetDialogComponent } from './delete-asset-dialog/delete-asset-dialog.component';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmFileReplaceDialogComponent } from './confirm-upload-dialog/confirm-file-replace-dialog.component';
import { ResetWorkspaceDialogComponent } from './reset-workspace-dialog/reset-workspace-dialog.component';
import { DeleteWorkspaceDialogComponent } from './delete-workspace-dialog/delete-workspace-dialog.component';
import { OAuthService } from '../../shared/services/oauth.service';
import { Splitter } from 'primeng/splitter';
import { Breadcrumb } from 'primeng/breadcrumb';
import { WorkspacePopoverComponent } from './workspace-popover/workspace-popover.component';
import { WorkspaceOptionsPopoverComponent } from './workspace-options-popover/workspace-options-popover.component';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Popover } from 'primeng/popover';

@Component({
  selector: 'app-data-maintenance',
  imports: [
    ButtonV3Component,
    ModalV2Module,
    DividerModule,
    ConfirmDialogModule,
    ProgressSpinnerComponent,
    Tree,
    TableV3Component,
    Skeleton,
    FilePreviewSidebarComponent,
    TooltipModule,
    Splitter,
    PageHeaderV3Component,
    PageContentLayoutV3Component,
    WorkspacePopoverComponent,
    WorkspaceOptionsPopoverComponent,
    SearchBarV3Component,
    Popover,
    Breadcrumb,
    SelectV2Module,
  ],
  templateUrl: './data-maintenance.component.html',
  styleUrl: './data-maintenance.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: RouteReuseStrategy, useClass: NoRouteReuseStrategy }],
})
export class DataMaintenanceComponent implements OnInit {
  readonly AssetType = AssetType;
  readonly tableId = 'assetsTable';

  count: number = 0;
  isSubmitting = signal<boolean>(false);
  isLoadingAssets = signal<boolean>(false);
  isPreviewOpen = signal<boolean>(false);
  previewAsset = signal<AssetTableRow | null>(null);
  isLoadingConfig = signal<boolean>(false);
  worktreeInfos: WorktreeInfo[] = [];
  activeWorktree = signal<string>('');
  uncommitedUserAssets = signal(0);
  selectedNodePath: string = '';
  fileReplaceMode = false;
  loading = signal(false);
  isCreatingWorkspace = signal(false);
  isLoadingWorkspaces = signal(false);

  isCopyingWorkspace = signal(false);
  /** Name of the workspace whose delete flow (checkonly pre-check -> confirmation -> actual delete) is in progress, or null. */
  checkingDeleteWorkspace = signal<string | null>(null);
  /** True while the actual DELETE request (after user confirmation) is in flight. */
  isConfirmingWorkspaceDelete = signal(false);
  /** True while the asset DELETE request (after user confirmation) is in flight. */
  isConfirmingAssetDelete = signal(false);

  // Remote workspace selection is UI only for now - no backend support yet.
  remoteWorkspaceOptions: SelectOption[] = [];
  selectedRemoteWorkspace: SelectOption | null = null;
  repositoryOptions: SelectOption[] = [];
  selectedRepository = signal<SelectOption | null>(null);
  private assetConfig: AssetConfigResponse | null = null;
  selectedRepositoryCanAccess = computed<boolean>(() => {
    const repositoryKey = this.selectedRepository()?.value;
    const repositories = this.assetConfig?.repositories ?? [];
    if (!repositoryKey) return repositories.length === 1 && repositories[0].canAccess;

    return repositories.some(
      (repository) => this.repositoryKeysMatch(repository.repositoryKey, repositoryKey) && repository.canAccess
    );
  });

  @ViewChild(WorkspacePopoverComponent) private workspacePopover?: WorkspacePopoverComponent;
  @ViewChild('repositoryPopover') private repositoryPopover?: Popover;

  selectedNode = signal<TreeNode | null>(null);
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  items: MenuItem[] = [{ label: 'View', icon: 'pi pi-search', command: () => this.showAsset(this.selectedNode()) }];

  assetTableData: AssetTableRow[] = [];

  workspaceOptions: MenuOption[] = [
    {
      label: 'Publish updates',
      icon: 'pi pi-send',
      action: () => this.submitPendingChanges(),
      enabledWhen: () => this.selectedRepositoryCanAccess() && !!this.activeWorktree() && this.uncommitedUserAssets() > 0,
    },
    {
      label: 'Reset workspace',
      icon: 'pi pi-undo',
      action: () => this.resetWorkspace(),
      enabledWhen: () => this.selectedRepositoryCanAccess() && !!this.activeWorktree() && this.uncommitedUserAssets() > 0,
    },
  ];

  resolvedWorkspaceOptions = computed(() => this.resolveOptions(this.workspaceOptions));

  get workspaceWorktrees(): WorktreeInfo[] {
    const repositoryKey = this.selectedRepository()?.value;
    return this.worktreeInfos.filter(
      (worktree) =>
        !repositoryKey ||
        worktree.repositoryKey === null ||
        (!!repositoryKey && this.repositoryKeysMatch(worktree.repositoryKey, repositoryKey))
    );
  }

  get worktrees(): string[] {
    return this.workspaceWorktrees.map((worktree) => worktree.worktree);
  }

  sortKey?: string;
  sortOrder?: string;

  page: number = 1;
  pageSize: number = 10;

  fileNameFilter = signal<string | undefined>(undefined);
  fileTypeFilter = signal<string | undefined>(undefined);
  uploadedByFilter = signal<string | undefined>(undefined);

  noResultsInfoText: string = 'No entries available yet';

  searchTerm: string = '';
  searchInput = new Subject<string>();

  selectedFolder: string = 'Assets';

  assetDataSubscription!: Subscription;

  columnDefinitions: ColumnDefinitionV3[] = [
    {
      displayName: '',
      key: 'overrideIndicator',
      width: 40,
      sortable: false,
      renderer: {
        component: OverrideIndicatorRendererComponent as ColumnRendererComponentV3<AssetTableRow>,
      },
    },
    {
      displayName: 'File Name',
      key: 'fileName',
      minWidth: 250,
      filter: {
        component: TextFilterV3Component,
      },
      renderer: {
        component: FileNameRendererComponent as ColumnRendererComponentV3<AssetTableRow>,
      },
    },
    {
      displayName: 'File Type',
      key: 'fileType',
      minWidth: 100,
      filter: {
        component: TextFilterV3Component,
      },
    },
    {
      displayName: 'Last Modified',
      key: 'lastModified',
      minWidth: 150,
    },
    {
      displayName: 'Uploaded By',
      key: 'uploadedBy',
      minWidth: 180,
      filter: {
        component: TextFilterV3Component,
      },
    },
    {
      displayName: 'Size',
      key: 'size',
      minWidth: 100,
    },
    {
      key: 'settings',
      width: 60,
      sortable: false,
      displayName: '',
      frozen: 'right',
      renderer: {
        component: OptionsTableMenuComponent<AssetTableRow> as ColumnRendererComponentV3<AssetTableRow>,
        config: {
          items: () => this.buildMenuItems(),
          visibleFor: (rowData: AssetTableRow) => {
            return !!rowData.fileType;
          },
        },
      },
    },
  ];

  destroy$ = new Subject<void>();
  private cancelUncommittedCount$ = new Subject<void>();
  assetTree = signal<TreeNode[]>([]);
  /** Key of the tree node whose assets are currently loading (shows a spinner in the tree). */
  loadingNodeKey = signal<string | null>(null);
  private selectedAsset: AssetTableRow | null = null;
  searchResults = signal<AssetSearchResult[]>([]);
  isSearchMode = signal<boolean>(false);
  isLoadingSearchResults = signal<boolean>(false);
  isLoadingMoreSearchResults = signal<boolean>(false);
  searchTotalCount = signal<number>(0);
  hasMoreSearchResults = computed<boolean>(() => this.searchResults().length < this.searchTotalCount());
  readonly searchPageSize = 20;
  private searchPage = 1;

  /** The search term backing the currently displayed results - used to highlight matches. */
  searchAppliedTerm = '';

  /** True while navigating to a search result (walking the tree down to the target folder).
   *  Exiting search mode re-creates the results table, which re-emits its table settings and
   *  would otherwise make the settings subscription call `loadAssets()` on the category root -
   *  cancelling the in-flight traversal and stranding the user at the top-level folder. This
   *  flag suppresses that so the traversal alone owns the asset loading while it runs. */
  private isNavigatingToSearchResult = false;

  constructor(
    private readonly dataMaintenanceService: DataMaintenanceService,
    private readonly oAuthService: OAuthService,
    private readonly dialogService: ComplexDialogV3Service,
    private readonly utilityService: UtilityService,
    private readonly destroyed: DestroyRef,
    private readonly snackbarService: SnackbarV3Service,
    private readonly tableService: TableServiceV3
  ) {}

  ngOnInit(): void {
    this.loadWorktrees();
    this.loadConfig();
    this.loadUncommittedCount();
    this.handleMenuItemClick();
    this.handleSearchChanged();
    // Settings or column config changes considered are sort, filter and page number changes
    this.handleHeaderClick();
  }

  loadUncommittedCount(): void {
    if (!this.activeWorktree()) return;

    this.isLoadingAssets.set(true);
    from(this.oAuthService.getToken())
      .pipe(
        switchMap((token) => this.dataMaintenanceService.getUncommitedAssetCount(token)),
        takeUntil(this.cancelUncommittedCount$),
        takeUntilDestroyed(this.destroyed),
        finalize(() => this.isLoadingAssets.set(false))
      )
      .subscribe({
        next: (res: DevOpsUncommitedCountResponse) => {
          this.uncommitedUserAssets.set(res.uncommitedCount);
        },
      });
  }

  private handleMenuItemClick(): void {
    this.tableService.actions.pipe(takeUntilDestroyed(this.destroyed)).subscribe((actionWrapper) => {
      this.selectedAsset = actionWrapper.rowData;
      if (this.selectedAsset) {
        switch (actionWrapper.action) {
          case AssetActionMenu.View:
            this.viewAsset();
            break;
          case AssetActionMenu.Replace:
            this.replaceExistingFile(this.selectedAsset);
            break;
          case AssetActionMenu.Download:
            this.downloadAsset();
            break;
          case AssetActionMenu.Rename:
            this.openRenameDialog();
            break;
        }
      }
    });
  }

  private replaceExistingFile(selectedAsset: AssetTableRow): void {
    if (!this.selectedRepositoryCanAccess()) return;

    this.fileReplaceMode = true;
    this.fileInput.nativeElement.accept = `.${selectedAsset.fileType}`;
    this.fileInput.nativeElement.click();
  }

  private handleHeaderClick(): void {
    this.tableService
      .getFilterTableSettings(this.tableId)
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe((settings) => {
        if (!settings?.columns) return;
        const sortedColumn = Object.keys(settings.columns).find((key) => settings.columns[key]?.sort !== '');

        if (sortedColumn) {
          this.sortKey = sortedColumn;
          this.sortOrder = settings.columns[sortedColumn]?.sort ?? 'asc';
        } else {
          this.sortKey = undefined;
          this.sortOrder = undefined;
        }

        this.pageSize = settings?.pageSize ?? this.pageSize;

        if (settings?.page !== undefined) {
          this.page = settings.page + 1;
        }

        const fileNameValue = settings.columns['fileName']?.filter?.[0]?.toString();
        this.fileNameFilter.set(fileNameValue && fileNameValue !== '' ? fileNameValue : undefined);

        const fileTypeValue = settings.columns['fileType']?.filter?.[0]?.toString();
        this.fileTypeFilter.set(fileTypeValue && fileTypeValue !== '' ? fileTypeValue : undefined);

        const uploadedByValue = settings.columns['uploadedBy']?.filter?.[0]?.toString();
        this.uploadedByFilter.set(uploadedByValue && uploadedByValue !== '' ? uploadedByValue : undefined);

        // While navigating to a search result the traversal owns asset loading. Exiting search
        // mode re-creates the results table, which re-emits settings here; letting that call
        // loadAssets() would cancel the in-flight traversal and strand us at the category root.
        if (this.isNavigatingToSearchResult) return;

        if (this.selectedNode()) this.loadAssets(this.selectedNode()!);
      });
  }

  loadWorktrees(): void {
    this.isLoadingWorkspaces.set(true);
    this.dataMaintenanceService
      .getWorktrees()
      .pipe(
        takeUntilDestroyed(this.destroyed),
        finalize(() => this.isLoadingWorkspaces.set(false))
      )
      .subscribe((response) => {
        this.worktreeInfos = response.worktrees;
        this.refreshWorktreesForRepository(false);
      });
  }

  /** Sets the given worktree as active. Pass `showGlobalSpinner = false` to avoid flashing the
   *  full-page spinner overlay (e.g. when the change is triggered from within the workspace popover). */
  onWorktreeChange(showGlobalSpinner = true): void {
    if (showGlobalSpinner) this.isSubmitting.set(true);
    if (this.activeWorktree()) {
      this.uncommitedUserAssets.set(0);
      this.dataMaintenanceService
        .setActiveWorktree(this.activeWorktree())
        .pipe(takeUntilDestroyed(this.destroyed))
        .subscribe({
          next: () => {
            if (showGlobalSpinner) this.isSubmitting.set(false);
            this.loadUncommittedCount();
            if (this.selectedNode()) this.loadAssets(this.selectedNode()!);
          },
          error: () => {
            if (showGlobalSpinner) this.isSubmitting.set(false);
          },
        });
    }
  }

  /** Handles the `createWorkspace` event emitted by the workspace popover component. */
  onCreateWorkspace(name: string): void {
    if (this.isCreatingWorkspace()) return;

    this.isCreatingWorkspace.set(true);
    this.dataMaintenanceService
      .createWorktree(name)
      .pipe(
        takeUntilDestroyed(this.destroyed),
        finalize(() => this.isCreatingWorkspace.set(false))
      )
      .subscribe({
        next: () => {
          this.addWorktreeInfo(name);
          this.workspacePopover?.clearCreateForm();
          // Set the newly created workspace as active without closing the popover or
          // flashing the full-page spinner overlay.
          this.activeWorktree.set(name);
          this.onWorktreeChange(false);
        },
        error: () => {
          this.snackbarService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to create workspace "${name}".`,
          });
        },
      });
  }

  /** Handles the `selectWorkspace` event emitted by the workspace popover component. */
  onSelectWorkspace(workspace: string): void {
    if (workspace !== this.activeWorktree.toString()) {
      this.activeWorktree.set(workspace);
      this.onWorktreeChange();
    }
  }

  /** Handles the `copyWorkspace` event emitted by the workspace popover component. */
  onCopyWorkspace({ source, destination }: { source: string; destination: string }): void {
    if (this.isCopyingWorkspace()) return;

    this.isCopyingWorkspace.set(true);
    this.dataMaintenanceService
      .copyWorkTree(source, destination, this.getSelectedRepositoryKey())
      .pipe(
        takeUntilDestroyed(this.destroyed),
        finalize(() => this.isCopyingWorkspace.set(false))
      )
      .subscribe({
        next: () => {
          this.addWorktreeInfo(destination);
          this.workspacePopover?.cancelCopyWorkspace();
          this.snackbarService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Workspace "${destination}" created from "${source}".`,
          });
        },
      });
  }

  /** Handles the `deleteWorkspace` event emitted by the workspace popover component.
   *  Runs a `checkonly` pre-check first, then either shows a confirmation dialog (deletable)
   *  or an informational dialog explaining why deletion is blocked. */
  onDeleteWorkspace(workspace: string): void {
    if (this.checkingDeleteWorkspace() || workspace === this.activeWorktree()) return;

    this.checkingDeleteWorkspace.set(workspace);
    from(this.oAuthService.getToken())
      .pipe(
        switchMap((token) => this.dataMaintenanceService.checkWorktreeDelete(workspace, token)),
        takeUntilDestroyed(this.destroyed)
      )
      .subscribe({
        next: (checkResult: WorktreeDeleteCheckResponse) => this.openDeleteWorkspaceDialog(workspace, checkResult),
        error: () => this.checkingDeleteWorkspace.set(null),
      });
  }

  private openDeleteWorkspaceDialog(workspace: string, checkResult: WorktreeDeleteCheckResponse): void {
    let dialogRef!: DynamicDialogRef;
    const { ref } = this.dialogService.open(
      {
        header: checkResult.canDelete ? 'Delete Workspace' : 'Cannot Delete Workspace',
        styleClass: 'folder-operation',
        hideButtons: true,
        dismissableMask: true,
        data: {
          workspace,
          checkResult,
          isDeleting: this.isConfirmingWorkspaceDelete,
          onConfirmDelete: () => this.performWorkspaceDelete(workspace, dialogRef),
        },
      },
      DeleteWorkspaceDialogComponent
    );
    dialogRef = ref;

    // The dialog only closes itself via Cancel/OK, or is closed by `performWorkspaceDelete`
    // once the deletion succeeds. Any close that wasn't a confirmed deletion means the delete
    // flow is over (cancelled, dismissed, or "Cannot Delete" acknowledged) - free up the icon.
    ref.onClose.pipe(takeUntilDestroyed(this.destroyed)).subscribe((confirmed: boolean | undefined) => {
      if (!confirmed) {
        this.checkingDeleteWorkspace.set(null);
      }
    });
  }

  private performWorkspaceDelete(workspace: string, dialogRef: DynamicDialogRef): void {
    if (this.isConfirmingWorkspaceDelete()) return;

    this.isConfirmingWorkspaceDelete.set(true);
    from(this.oAuthService.getToken())
      .pipe(
        switchMap((token) => this.dataMaintenanceService.deleteWorktree(token, workspace)),
        takeUntilDestroyed(this.destroyed),
        finalize(() => this.isConfirmingWorkspaceDelete.set(false))
      )
      .subscribe({
        next: () => {
          this.checkingDeleteWorkspace.set(null);
          dialogRef.close(true);
          this.loadWorktrees();
          this.snackbarService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Workspace "${workspace}" deleted successfully.`,
          });
        },
        error: () => {
          this.snackbarService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to delete workspace "${workspace}". Please try again.`,
          });
        },
      });
  }

  loadConfig(): void {
    this.isLoadingConfig.set(true);
    this.isLoadingAssets.set(true);
    from(this.oAuthService.getToken())
      .pipe(switchMap((token) => this.dataMaintenanceService.getConfig(token)))
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe({
        next: (res: AssetConfigResponse) => {
          this.assetConfig = res;
          this.repositoryOptions = (res.repositories ?? []).map((repository) => ({
            name: repository.displayName,
            value: repository.repositoryKey,
          }));
          this.selectedRepository.set(this.repositoryOptions[0] ?? null);
          this.refreshWorktreesForRepository(false);
          this.constructTreeFromConfig(res);
          const firstNode = this.assetTree()[0];
          if (firstNode) {
            this.selectedNode.set(firstNode);
            this.onNodeSelect(firstNode);
          }
          this.isLoadingConfig.set(false);
        },
        error: (err) => {
          this.isLoadingConfig.set(false);
          console.error(err);
        },
      });
  }

  onRepositoryChange(repository: SelectOption): void {
    this.selectedRepository.set(repository);
    this.selectedNode.set(null);
    this.selectedNodePath = '';
    this.selectedFolder = 'Assets';
    this.assetTableData = [];
    this.count = 0;

    if (!this.assetConfig) return;

    this.isLoadingWorkspaces.set(true);
    this.isLoadingAssets.set(true);
    this.isLoadingConfig.set(true);
    this.dataMaintenanceService
      .getWorktrees()
      .pipe(
        takeUntilDestroyed(this.destroyed),
        finalize(() => {
          this.isLoadingWorkspaces.set(false);
          this.isLoadingAssets.set(false);
          this.isLoadingConfig.set(false);
        })
      )
      .subscribe({
        next: (response) => {
          this.worktreeInfos = response.worktrees;
          this.refreshWorktreesForRepository(true);
          this.constructTreeFromConfig(this.assetConfig!);
          const firstNode = this.assetTree()[0];
          if (firstNode) {
            this.selectedNode.set(firstNode);
            this.onNodeSelect(firstNode);
          }
        },
        error: () => {
          this.snackbarService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to refresh workspaces for the selected repository.',
          });
        },
      });
  }

  private refreshWorktreesForRepository(repositoryChanged: boolean): void {
    const repositoryKey = this.selectedRepository()?.value;
    const currentWorktree = this.activeWorktree();
    const compatibleWorktrees = this.worktreeInfos.filter((worktree) => this.isWorktreeCompatible(worktree, repositoryKey));

    if (!repositoryKey) {
      this.activeWorktree.set('');
      return;
    }

    const currentWorktreeInfo = compatibleWorktrees.find((worktree) => worktree.worktree === currentWorktree);
    const nextWorktreeInfo =
      currentWorktreeInfo ??
      compatibleWorktrees.find((worktree) => worktree.repositoryKey === null) ??
      compatibleWorktrees.find((worktree) => this.repositoryKeysMatch(worktree.repositoryKey!, repositoryKey));
    const nextWorktree = nextWorktreeInfo?.worktree ?? '';

    if (nextWorktree === currentWorktree) return;

    this.activeWorktree.set(nextWorktree);

    if (repositoryChanged) {
      this.snackbarService.add({
        severity: 'info',
        summary: nextWorktree ? 'Workspace changed' : 'No workspace available',
        detail: nextWorktree
          ? `The active workspace was changed to "${nextWorktree}" because it is available for the selected repository.`
          : 'No workspace exists for this repository. Create a new workspace to continue.',
      });
    }

    if (nextWorktree && nextWorktree !== currentWorktree) {
      this.onWorktreeChange(false);
    }
  }

  private isWorktreeCompatible(worktree: WorktreeInfo, repositoryKey: string | undefined): boolean {
    return (
      worktree.repositoryKey === null || (!!repositoryKey && this.repositoryKeysMatch(worktree.repositoryKey, repositoryKey))
    );
  }

  private addWorktreeInfo(worktree: string): void {
    this.worktreeInfos = [...this.worktreeInfos, { worktree, repositoryKey: null }];
  }

  private updateWorktreeRepository(worktree: string, repositoryKey: string): void {
    const worktreeInfo = this.worktreeInfos.find((item) => item.worktree === worktree);
    if (!worktreeInfo) {
      this.worktreeInfos = [...this.worktreeInfos, { worktree, repositoryKey }];
      return;
    }

    this.worktreeInfos = this.worktreeInfos.map((item) => (item.worktree === worktree ? { ...item, repositoryKey } : item));
  }

  private repositoryKeysMatch(first: string, second: string): boolean {
    return first.trim().toLowerCase() === second.trim().toLowerCase();
  }

  toggleRepositoryPopover(event: Event): void {
    this.repositoryPopover?.toggle(event);
  }

  selectRepository(repository: SelectOption): void {
    this.repositoryPopover?.hide();
    if (repository.value !== this.selectedRepository()?.value) {
      this.onRepositoryChange(repository);
    }
  }

  repositoryCanAccess(repository: SelectOption): boolean {
    return (this.assetConfig?.repositories ?? []).some(
      (configuredRepository) =>
        this.repositoryKeysMatch(configuredRepository.repositoryKey, repository.value) && configuredRepository.canAccess
    );
  }

  searchValueChanged(newValue: string): void {
    this.searchTerm = (newValue ?? '').trim();
    this.searchInput.next(this.searchTerm);
  }

  onSearchButtonClicked(): void {
    this.performSearch();
  }

  private handleSearchChanged(): void {
    this.searchInput.pipe(takeUntilDestroyed(this.destroyed), debounceTime(500)).subscribe((searchTerm: string) => {
      if (searchTerm.length >= 3) {
        this.performSearch();
      } else if (searchTerm.length === 0) {
        this.isSearchMode.set(false);
        this.searchResults.set([]);
        this.searchTotalCount.set(0);
      }
    });
  }

  private performSearch(): void {
    if (!this.searchTerm || this.searchTerm.length < 3) return;
    this.searchAppliedTerm = this.searchTerm.trim();
    this.isSearchMode.set(true);
    this.isLoadingSearchResults.set(true);
    this.searchPage = 1;
    this.dataMaintenanceService
      .searchAssets(this.searchTerm, this.searchPage, this.searchPageSize, this.getSelectedRepositoryKey())
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe({
        next: (response: AssetSearchResponse) => {
          this.searchResults.set(response.results);
          this.searchTotalCount.set(response.totalCount);
          this.isLoadingSearchResults.set(false);
        },
        error: () => {
          this.searchResults.set([]);
          this.searchTotalCount.set(0);
          this.isLoadingSearchResults.set(false);
        },
      });
  }

  /** Loads and appends the next page of search results (infinite scroll). */
  loadMoreSearchResults(): void {
    if (this.isLoadingSearchResults() || this.isLoadingMoreSearchResults() || !this.hasMoreSearchResults()) {
      return;
    }
    this.isLoadingMoreSearchResults.set(true);
    this.searchPage += 1;
    this.dataMaintenanceService
      .searchAssets(this.searchTerm, this.searchPage, this.searchPageSize, this.getSelectedRepositoryKey())
      .pipe(
        takeUntilDestroyed(this.destroyed),
        finalize(() => this.isLoadingMoreSearchResults.set(false))
      )
      .subscribe({
        next: (response: AssetSearchResponse) => {
          this.searchResults.set([...this.searchResults(), ...response.results]);
          this.searchTotalCount.set(response.totalCount);
        },
        error: () => {
          // Revert the page increment so the next scroll retries the same page.
          this.searchPage -= 1;
        },
      });
  }

  /** Triggers loading the next page when the results list is scrolled near the bottom. */
  onSearchResultsScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (distanceFromBottom <= 100) {
      this.loadMoreSearchResults();
    }
  }

  getSearchResultBreadcrumb(result: AssetSearchResult): MenuItem[] {
    const segments = result.path.split('/').filter(Boolean);
    return segments.map((segment) => ({ label: segment }));
  }

  /** Wraps each occurrence of the applied search term in `fileName` with a highlight span.
   *  All dynamic text is HTML-escaped and the only injected markup is a static class-bearing
   *  span, so the returned string is safe to bind via [innerHTML]. */
  highlightSearchTerm(fileName: string): string {
    const term = this.searchAppliedTerm?.trim();
    if (!term) {
      return this.escapeHtml(fileName);
    }

    const lowerName = fileName.toLowerCase();
    const lowerTerm = term.toLowerCase();
    let html = '';
    let index = 0;
    let matchIndex = lowerName.indexOf(lowerTerm, index);
    while (matchIndex !== -1) {
      html += this.escapeHtml(fileName.slice(index, matchIndex));
      html += `<span class="search-highlight">${this.escapeHtml(fileName.slice(matchIndex, matchIndex + term.length))}</span>`;
      index = matchIndex + term.length;
      matchIndex = lowerName.indexOf(lowerTerm, index);
    }
    html += this.escapeHtml(fileName.slice(index));
    return html;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  onSearchResultClick(result: AssetSearchResult): void {
    // Exit search mode
    this.isSearchMode.set(false);
    this.searchResults.set([]);
    this.searchTotalCount.set(0);

    // A folder hit navigates into the folder itself; a file hit navigates to the folder that
    // contains it (by stripping the file name from the path).
    const pathSegments = result.path.split('/').filter(Boolean);
    const targetFolderPath = result.isFolder ? pathSegments.join('/') : pathSegments.slice(0, -1).join('/');

    if (!targetFolderPath) return;

    // Find the category node whose path prefixes the target folder path
    const categoryNode = this.assetTree().find((node) => node.data?.path && this.isPathOnRoute(node.data.path, targetFolderPath));

    if (!categoryNode) return;

    // Cancel any in-flight asset request once before starting the traversal.
    if (this.assetDataSubscription && !this.assetDataSubscription.closed) {
      this.assetDataSubscription.unsubscribe();
    }

    // Guard the traversal against the table-settings subscription (see the flag's declaration).
    this.isNavigatingToSearchResult = true;
    this.traverseToFolder(categoryNode, targetFolderPath);
  }

  /**
   * Loads the assets for `node`, then walks down its freshly loaded child folders
   * toward `targetFolderPath`, loading each level in turn. Matching is done on the
   * folder path (the reliable identifier) rather than the display label, since labels
   * may differ from path segments (e.g. "Baseline RedGenium" vs "Baseline_RedGenium").
   *
   * The previous subscription is intentionally not cancelled here: doing so on each
   * recursive step aborts the request whose response is still being delivered, which
   * surfaces as a server-side OperationCanceledException. Cancellation is handled once
   * by the caller before the traversal starts.
   */
  private traverseToFolder(node: TreeNode, targetFolderPath: string): void {
    this.selectedNodePath = node.data.path;
    this.selectedFolder = node.label ?? '';
    this.selectedNode.set(node);
    this.isLoadingAssets.set(true);
    this.noResultsInfoText = 'No entries available yet';

    const repositoryKey = this.getRepositoryKeyForNode(node);
    if (!repositoryKey) {
      this.assetTableData = [];
      this.count = 0;
      this.noResultsInfoText = 'Repository information is unavailable.';
      this.isLoadingAssets.set(false);
      return;
    }

    this.loadingNodeKey.set(node.key ?? null);
    this.assetDataSubscription = this.dataMaintenanceService
      .getAllAssets(this.selectedNodePath, repositoryKey, this.sortKey, this.sortOrder, this.page, this.pageSize)
      .pipe(
        finalize(() => {
          this.clearLoadingNode(node);
          this.isNavigatingToSearchResult = false;
        }),
        takeUntilDestroyed(this.destroyed)
      )
      .subscribe({
        next: (assetListResponse: DataMaintenanceAssetResponse) => {
          this.createChildrenNodes(node, assetListResponse.folders);

          // Continue traversal only if this node isn't the target folder yet.
          if (node.data.path !== targetFolderPath) {
            const childNode = node.children?.find((c) => c.data?.path && this.isPathOnRoute(c.data.path, targetFolderPath));
            if (childNode) {
              this.traverseToFolder(childNode, targetFolderPath);
              return;
            }
          }
          this.assetTableData = this.transformAssetsToTableData(assetListResponse.assets);
          this.count = assetListResponse.totalCount;
          if (assetListResponse.activeWorktree && this.worktrees.includes(assetListResponse.activeWorktree)) {
            this.activeWorktree.set(assetListResponse.activeWorktree);
          }
          this.isLoadingAssets.set(false);
        },
        error: () => {
          this.assetTableData = [];
          this.noResultsInfoText = 'Failed to load data.';
          this.isLoadingAssets.set(false);
        },
      });
  }

  /** True when `candidatePath` is the target folder path or an ancestor of it. */
  private isPathOnRoute(candidatePath: string, targetFolderPath: string): boolean {
    return targetFolderPath === candidatePath || targetFolderPath.startsWith(`${candidatePath}/`);
  }

  private constructTreeFromConfig(data: AssetConfigResponse): void {
    const treeNodes: TreeNode[] = [];

    // Iterate through assetCategories
    const selectedRepositoryKey = this.selectedRepository()?.value;
    Object.values(data.assetCategories)
      .filter(
        (category) =>
          !selectedRepositoryKey || category.repositoryKey?.trim().toLowerCase() === selectedRepositoryKey.trim().toLowerCase()
      )
      .forEach((category) => {
        const categoryNode: TreeNode = {
          key: category.id,
          label: category.displayName,
          expanded: false,
          data: {
            id: category.id,
            isCategory: true,
            validationRules: category.validationRules,
            path: category.pathPrefix,
            repositoryKey: category.repositoryKey,
            description: category.description,
          },
          children: [],
        };

        treeNodes.push(categoryNode);
      });

    this.assetTree.set(treeNodes);
  }

  onNodeSelect(node: TreeNode): void {
    this.selectedFolder = node.label ?? '';
    this.selectedNode.set(node);

    if (node.data?.path) {
      this.selectedNodePath = node.data.path;
    }

    node.children = [];
    this.isLoadingAssets.set(true);
    this.noResultsInfoText = 'No entries available yet';

    const settings = this.tableService.getFilterTableSettingsSnapshot(this.tableId);
    if (settings && settings.page != 0) {
      settings.page = 0;
      this.tableService.setFilterTableSettings(this.tableId, settings);
    } else {
      this.loadAssets(node);
    }
  }

  loadAssets(node: TreeNode): void {
    this.isLoadingAssets.set(true);
    this.isSearchMode.set(false);
    this.noResultsInfoText = 'No entries available yet';

    if (this.selectedNodePath === '') {
      this.assetTableData = [];
      this.count = 0;
      this.isLoadingAssets.set(false);
      return;
    }
    const repositoryKey = this.getRepositoryKeyForNode(node);
    if (!repositoryKey) {
      this.assetTableData = [];
      this.count = 0;
      this.noResultsInfoText = 'Repository information is unavailable.';
      this.isLoadingAssets.set(false);
      return;
    }
    if (this.assetDataSubscription && !this.assetDataSubscription.closed) {
      this.assetDataSubscription.unsubscribe();
    }
    this.loadingNodeKey.set(node.key ?? null);
    this.assetDataSubscription = this.dataMaintenanceService
      .getAllAssets(
        this.selectedNodePath,
        repositoryKey,
        this.sortKey,
        this.sortOrder,
        this.page,
        this.pageSize,
        this.fileNameFilter(),
        this.fileTypeFilter(),
        this.uploadedByFilter()
      )
      .pipe(
        finalize(() => this.clearLoadingNode(node)),
        takeUntilDestroyed(this.destroyed)
      )
      .subscribe({
        next: (assetListResponse: DataMaintenanceAssetResponse) => {
          this.createChildrenNodes(node, assetListResponse.folders);
          this.assetTableData = this.transformAssetsToTableData(assetListResponse.assets);
          node.expanded = true;
          this.selectedNode.set(node);
          this.count = assetListResponse.totalCount;
          if (assetListResponse.activeWorktree) {
            const previousActiveWorktree = this.activeWorktree();
            const worktree = assetListResponse.activeWorktree;
            const repositoryKey = this.getRepositoryKeyForNode(node);
            const worktreeInfo = this.worktreeInfos.find((item) => item.worktree === worktree);
            if (!worktreeInfo) {
              this.addWorktreeInfo(worktree);
            }

            const resolvedWorktreeInfo = this.worktreeInfos.find((item) => item.worktree === worktree);
            if (resolvedWorktreeInfo && this.isWorktreeCompatible(resolvedWorktreeInfo, repositoryKey)) {
              this.activeWorktree.set(worktree);
            }

            if (this.activeWorktree() !== previousActiveWorktree) {
              this.uncommitedUserAssets.set(0);
              this.loadUncommittedCount();
            }
          }
          this.isLoadingAssets.set(false);
        },
        error: () => {
          this.assetTableData = [];
          this.noResultsInfoText = 'Failed to load data.';
          this.isLoadingAssets.set(false);
        },
      });
  }

  private createChildrenNodes(selectedNode: TreeNode, assets: DataMaintenanceAsset[]): void {
    const children: TreeNode[] = [];
    for (const asset of assets) {
      const childNode: TreeNode = {
        key: `${selectedNode.key}-${asset.fileName}`,
        label: asset.fileName,
        expanded: false,
        data: {
          name: asset.fileName,
          path: asset.path,
          repositoryKey: asset.repositoryKey,
          isFolder: true,
        },
        children: [],
      };
      children.push(childNode);
    }

    if (children.length > 0) {
      selectedNode.children = children;
    }

    selectedNode.expanded = true;
    this.assetTree.set([...this.assetTree()]);
  }

  /** Clears the tree spinner, unless a newer request has already claimed it for another node. */
  private clearLoadingNode(node: TreeNode): void {
    if (this.loadingNodeKey() === node.key) {
      this.loadingNodeKey.set(null);
    }
  }

  private showAsset(selectedAsset: TreeNode | null): void {
    const assetPath = selectedAsset?.data?.path;
    const repositoryKey = selectedAsset ? selectedAsset.data?.repositoryKey ?? this.getRepositoryKeyForNode(selectedAsset) : '';
    if (assetPath && repositoryKey) {
      this.utilityService.openAsset(assetPath, repositoryKey);
    }
  }

  private transformAssetsToTableData(assets: DataMaintenanceAsset[]): AssetTableRow[] {
    return assets.map((asset) => {
      const fileType = this.getFileExtension(asset.fileName);
      const size = asset.size || '';

      return {
        categoryId: this.findCategoryForAssetPath(asset.path)?.id,
        repositoryKey: asset.repositoryKey,
        fileName: asset.fileName,
        fileType: fileType,
        lastModified: this.formatDate(asset.lastModified ? new Date(asset.lastModified) : new Date()),
        uploadedBy: asset.uploadedBy || '',
        size: size,
        path: asset.path,
        isUserOverride: asset.isUserOverride || false,
        isFolder: false,
        hasChildren: false,
      };
    });
  }

  private getSelectedRepositoryKey(): string {
    const selectedNode = this.selectedNode();
    return selectedNode ? this.getRepositoryKeyForNode(selectedNode) : '';
  }

  /** Resolves the repository key from the node itself, falling back to its topmost parent (category) node. */
  private getRepositoryKeyForNode(node: TreeNode): string {
    const rootData = this.getTopmostParentData(node);
    return node.data?.repositoryKey ?? rootData?.repositoryKey ?? '';
  }

  private getFileExtension(fileName: string): string {
    // Preserve custom compound extensions (e.g. ".data.json", ".table.json", ".form.json")
    // so the reported file type reflects the real type instead of just "json".
    const compoundMatch = fileName.match(/\.([^.]+\.json)$/i);
    if (compoundMatch) {
      return compoundMatch[1].toLowerCase();
    }

    const parts = fileName.split('.');
    return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
  }

  private formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  private buildMenuItems(): {
    icon: string;
    text: string;
    action: AssetActionMenu;
    visible: (row: AssetTableRow) => boolean;
    disabledForFunction?: (row: AssetTableRow) => boolean;
    disabledTranslationKey?: string;
  }[] {
    return [
      {
        icon: 'path-minus',
        text: 'Replace',
        action: AssetActionMenu.Replace,
        visible: (row: AssetTableRow) => !!row.path && !!this.activeWorktree(),
        disabledForFunction: (row: AssetTableRow) => !this.selectedRepositoryCanAccess() || !this.isReplaceAllowedForAsset(row),
        disabledTranslationKey: 'This repository is view only or the file type cannot be replaced in this category',
      },
      {
        icon: 'search',
        text: 'View',
        action: AssetActionMenu.View,
        visible: (row: AssetTableRow) => !!row.path,
      },
      {
        icon: 'download',
        text: 'Download',
        action: AssetActionMenu.Download,
        visible: (row: AssetTableRow) => !!row.path,
      },
      {
        icon: 'edit',
        text: 'Rename',
        action: AssetActionMenu.Rename,
        visible: (row: AssetTableRow) => !!row.path,
        disabledForFunction: (row: AssetTableRow) =>
          !this.selectedRepositoryCanAccess() || !this.isUploadAllowedForAsset(row) || !this.isReplaceAllowedForAsset(row),
        disabledTranslationKey: 'This repository is view only or renaming is not allowed for this file type or category',
      },
    ];
  }

  private viewAsset(): void {
    if (this.selectedAsset?.path) {
      const repositoryKey = this.selectedAsset.repositoryKey ?? this.getSelectedRepositoryKey();
      if (repositoryKey) {
        this.utilityService.openAsset(this.selectedAsset.path, repositoryKey);
      }
    }
  }

  private downloadAsset(): void {
    const path = this.selectedAsset?.path;
    if (!path) return;

    const asset = this.selectedAsset!;

    this.snackbarService.add({
      severity: 'info',
      summary: 'Download',
      detail: `Downloading ${asset.fileName}...`,
    });

    this.dataMaintenanceService
      .downloadAsset(path, asset.repositoryKey || this.getSelectedRepositoryKey())
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe({
        next: (response) => {
          if (!response.body) return;
          saveAs(response.body, asset.fileName ?? 'download');
        },
        error: (err) => {
          console.error(`Failed to download ${asset.fileName}.`, err);
        },
      });
  }

  private openRenameDialog(): void {
    if (!this.selectedRepositoryCanAccess() || !this.selectedAsset?.path) return;

    const { ref } = this.dialogService.open(
      {
        header: 'Rename File',
        styleClass: 'folder-operation',
        hideButtons: true,
        dismissableMask: true,
        data: {
          currentFileName: this.selectedAsset.fileName,
          assetPath: this.selectedAsset.path.replace(/\\/g, '/'),
          isUserOverride: this.selectedAsset.isUserOverride ?? false,
          categoryId: this.selectedAsset.categoryId ?? this.findCategoryForAssetPath(this.selectedAsset.path)?.id ?? '',
          repositoryKey: this.selectedAsset.repositoryKey ?? this.getSelectedRepositoryKey(),
        },
      },
      RenameDialogComponent
    );

    ref.onClose.pipe(takeUntilDestroyed(this.destroyed)).subscribe((result: { newFileName: string } | undefined) => {
      if (result?.newFileName && this.selectedNode()) {
        this.loadUncommittedCount();
        this.loadAssets(this.selectedNode()!);
      }
    });
  }

  onRowClicked(row: AssetTableRow): void {
    this.selectedAsset = row;
    // Open if it's a folder
    if (!row.fileType && row.path) {
      const nodeToSelect: TreeNode | null = this.findNodeByPath(this.selectedNode(), row.path);
      if (nodeToSelect) {
        this.onNodeSelect(nodeToSelect);
      }
    } else {
      this.openPreviewSidebar(row);
    }
  }

  private findNodeByPath(selectedNode: TreeNode | null, path: string): TreeNode | null {
    if (selectedNode?.data?.path === path) {
      return selectedNode;
    }

    if (selectedNode?.children) {
      for (const child of selectedNode.children) {
        const result = this.findNodeByPath(child, path);
        if (result) {
          return result;
        }
      }
    }

    return null;
  }

  previewAssetAllowUpload = computed<boolean>(
    () => this.selectedRepositoryCanAccess() && this.isUploadAllowedForAsset(this.previewAsset())
  );
  previewAssetCanReplace = computed<boolean>(
    () => this.selectedRepositoryCanAccess() && this.isReplaceAllowedForAsset(this.previewAsset())
  );

  private isUploadAllowedForAsset(asset: AssetTableRow | null): boolean {
    if (!asset?.path) return true;

    return this.findValidationRulesForPath(asset.path)?.allowUpload ?? true;
  }

  private isReplaceAllowedForAsset(asset: AssetTableRow | null): boolean {
    if (!asset?.fileName) return false;

    const allowedExtensions = this.findValidationRulesForPath(asset.path)?.allowedExtensions;
    if (!allowedExtensions?.length) return false;

    const fileName = asset.fileName.toLowerCase();
    return allowedExtensions.some((extension) => fileName.endsWith(extension.toLowerCase()));
  }

  private findValidationRulesForPath(path: string | undefined): ValidationRules | undefined {
    if (!path) return undefined;

    const categoryNode = this.assetTree().find((node) => node.data?.path && this.isPathOnRoute(node.data.path, path));

    return categoryNode?.data?.validationRules;
  }

  openPreviewSidebar(row: AssetTableRow): void {
    this.previewAsset.set(row);
    this.isPreviewOpen.set(true);
  }

  closePreviewSidebar(): void {
    this.isPreviewOpen.set(false);
    this.previewAsset.set(null);
  }

  handleSidebarReplace(asset: AssetTableRow): void {
    this.selectedAsset = asset;
    this.replaceExistingFile(asset);
  }

  handleSidebarRename(asset: AssetTableRow): void {
    this.selectedAsset = asset;
    this.openRenameDialog();
  }

  handleSidebarDownload(asset: AssetTableRow): void {
    this.selectedAsset = asset;
    this.downloadAsset();
  }

  handleSidebarDelete(asset: AssetTableRow): void {
    if (!this.selectedRepositoryCanAccess()) return;

    let dialogRef!: DynamicDialogRef;
    const { ref } = this.dialogService.open(
      {
        header: 'Delete File',
        styleClass: 'custom-dialog',
        hideButtons: true,
        dismissableMask: true,
        data: {
          asset,
          categoryId: asset.categoryId ?? this.findCategoryForAssetPath(asset.path)?.id ?? '',
          isDeleting: this.isConfirmingAssetDelete,
          onConfirmDelete: () => this.performAssetDelete(asset, dialogRef),
        },
      },
      DeleteAssetDialogComponent
    );
    dialogRef = ref;
  }

  private performAssetDelete(asset: AssetTableRow, dialogRef: DynamicDialogRef): void {
    if (!asset.path || this.isConfirmingAssetDelete()) return;

    const assetPath = asset.path.replace(/\\/g, '/');
    this.isConfirmingAssetDelete.set(true);
    this.loading.set(true);
    from(this.oAuthService.getToken())
      .pipe(
        switchMap((token) =>
          this.dataMaintenanceService.deleteUserAsset(
            assetPath,
            asset.categoryId ?? this.findCategoryForAssetPath(asset.path)?.id ?? '',
            asset.repositoryKey ?? this.getSelectedRepositoryKey(),
            token
          )
        ),
        takeUntilDestroyed(this.destroyed),
        finalize(() => {
          this.isConfirmingAssetDelete.set(false);
          this.loading.set(false);
        })
      )
      .subscribe({
        next: () => {
          dialogRef.close(true);
          this.snackbarService.add({
            severity: 'success',
            summary: 'Success',
            detail: `File "${asset.fileName}" deleted successfully.`,
          });
          this.loadUncommittedCount();
          this.closePreviewSidebar();
          if (this.selectedNode()) this.loadAssets(this.selectedNode()!);
        },
      });
  }

  private findCategoryForAssetPath(path: string | undefined): AssetCategory | undefined {
    if (!path) return undefined;

    const categoryNode = this.assetTree().find((node) => node.data?.path && this.isPathOnRoute(node.data.path, path));
    return categoryNode?.data;
  }

  handleUndoChanges(asset: AssetTableRow): void {
    if (!this.selectedRepositoryCanAccess()) return;

    const { ref } = this.dialogService.open(
      {
        header: 'Undo Changes',
        styleClass: 'custom-dialog',
        hideButtons: true,
        dismissableMask: true,
        data: asset,
      },
      RevertAssetDialogComponent
    );

    ref.onClose.pipe(takeUntilDestroyed(this.destroyed)).subscribe((confirmed: any) => {
      if (
        confirmed &&
        !this.utilityService.isExitEventFromGEADialog(confirmed) &&
        !this.utilityService.isRejectEventFromGEADialog(confirmed)
      ) {
        this.performAssetRevert(asset.path!);
      }
    });
  }

  private performAssetRevert(assetPath: string): void {
    this.loading.set(true);
    from(this.oAuthService.getToken())
      .pipe(
        switchMap((token) => this.dataMaintenanceService.resetAsset(token, assetPath)),
        takeUntilDestroyed(this.destroyed),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: () => {
          this.snackbarService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Asset reverted successfully',
          });
          this.uncommitedUserAssets.set(0);
          this.loadUncommittedCount();
          this.closePreviewSidebar();
          this.loadAssets(this.selectedNode()!);
        },
        error: () => {
          this.snackbarService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to revert asset',
          });
        },
      });
  }

  async submitPendingChanges(): Promise<void> {
    if (!this.selectedRepositoryCanAccess()) return;

    const { ref } = this.dialogService.open(
      {
        header: 'Submit Changes',
        styleClass: 'custom-dialog',
        hideButtons: true,
        dismissableMask: true,
        data: { repositoryKey: this.getSelectedRepositoryKey() },
      },
      CommitDialogComponent
    );

    ref.onClose.pipe(takeUntilDestroyed(this.destroyed)).subscribe((commitResult: DevOpsCommitResponse) => {
      if (!commitResult || this.utilityService.isExitEventFromGEADialog(commitResult)) return;

      if (!commitResult.pullRequestUrl) {
        this.snackbarService.add({
          severity: 'error',
          summary: 'Submission incomplete',
          detail: 'The commit succeeded but no pull request URL was returned. Pending changes were not cleared.',
        });
        return;
      }

      window.open(commitResult.pullRequestUrl, '_blank');

      this.snackbarService.add({
        severity: 'success',
        summary: `Assets submitted to ${commitResult.repositoryDisplayName}`,
        detail: commitResult.successMessage,
      });

      const selectedRepositoryKey = this.getSelectedRepositoryKey();
      if (!selectedRepositoryKey || this.repositoryKeysMatch(selectedRepositoryKey, commitResult.repositoryKey)) {
        this.uncommitedUserAssets.set(0);
        this.loadUncommittedCount();
      }
    });
  }

  resetWorkspace(): void {
    if (!this.selectedRepositoryCanAccess()) return;

    const { ref } = this.dialogService.open(
      {
        header: 'Reset Workspace',
        styleClass: 'folder-operation',
        hideButtons: true,
        dismissableMask: true,
      },
      ResetWorkspaceDialogComponent
    );

    ref.onClose.pipe(takeUntilDestroyed(this.destroyed)).subscribe((confirmed: any) => {
      if (confirmed && !this.utilityService.isExitEventFromGEADialog(confirmed)) {
        this.performWorkspaceReset();
      }
    });
  }

  private performWorkspaceReset(): void {
    this.isSubmitting.set(true);
    from(this.oAuthService.getToken())
      .pipe(
        switchMap((token) => this.dataMaintenanceService.resetWorkspace(token)),
        takeUntilDestroyed(this.destroyed),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe({
        next: (result) => {
          this.snackbarService.add({
            severity: 'success',
            summary: 'Workspace Reset',
            detail: `Deleted ${result.deletedCount} file(s), restored ${result.restoredCount} file(s) from remote.`,
          });
          this.uncommitedUserAssets.set(0);
          this.loadUncommittedCount();
          if (this.selectedNode()) {
            this.loadAssets(this.selectedNode()!);
          }
        },
        error: () => {
          this.snackbarService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to reset workspace',
          });
        },
      });
  }

  onNodeUnselect(): void {
    this.selectedNode.set(null);
  }

  onFileUpload(): void {
    if (this.selectedRepositoryCanAccess() && this.selectedNode()) {
      this.fileReplaceMode = false;
      const category: AssetCategory = this.getTopmostParentData(this.selectedNode()!);
      this.fileInput.nativeElement.accept = category.validationRules.allowedExtensions.join(',');
      this.fileInput.nativeElement.click();
    }
  }

  validateFileSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (this.assetTableData.length && !this.fileReplaceMode) {
        const baseAsset = this.assetTableData.find((asset) => asset.fileName === file.name);
        if (baseAsset) {
          this.selectedAsset = baseAsset;
          this.showFileReplaceDialog(event);
        } else {
          this.onFileSelection(event);
        }
      } else {
        this.onFileSelection(event);
      }
    }
  }

  private showFileReplaceDialog(event: Event): void {
    const input = event.target as HTMLInputElement;
    const { ref } = this.dialogService.open(
      {
        header: 'File exists',
        styleClass: 'custom-dialog',
        hideButtons: true,
        dismissableMask: true,
      },
      ConfirmFileReplaceDialogComponent
    );
    ref.onClose.pipe(takeUntilDestroyed(this.destroyed)).subscribe((confirmation) => {
      if (confirmation && !this.utilityService.isExitEventFromGEADialog(confirmation)) {
        this.fileReplaceMode = true;
        this.onFileSelection(event);
      } else {
        if (input) input.value = '';
      }
    });
  }

  onFileSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (!this.selectedNode()) {
        return;
      }
      const category: AssetCategory = this.getTopmostParentData(this.selectedNode()!);
      const maxFileSizeInBytes = category.validationRules.maxSizeMB * 1024 * 1024;
      const errorMessages = this.checkFileForErrors(file, category.validationRules.maxSizeMB);

      if (errorMessages.length) {
        this.snackbarService.add(errorMessages[0]);
        return;
      }

      if (file.size > maxFileSizeInBytes) {
        this.snackbarService.add({
          severity: 'info',
          summary: 'Error',
          detail: `File size exceeds the maximum allowed size of ${category.validationRules.maxSizeMB}MB`,
        });
        input.value = '';
        return;
      }
      this.uploadFile(file);
      // Reset input to allow selecting the same file again
      input.value = '';
    }
  }

  checkFileForErrors(file: File, maxFileSizeMB: number): SnackbarMessage[] {
    const maxFileSizeInBytes = maxFileSizeMB * 1024 * 1024;
    const errorMessages: SnackbarMessage[] = [];
    const baseSnackbarMessage: SnackbarMessage = {
      severity: 'info',
      summary: 'Error',
      detail: '',
    };
    const fileExtensions = this.getValidFileExtensions();
    if (!fileExtensions.includes('.' + this.getFileExtension(file.name))) {
      errorMessages.push({ ...baseSnackbarMessage, detail: `Allowed file extensions: ${fileExtensions}` });
    }
    if (file.size > maxFileSizeInBytes) {
      errorMessages.push({ ...baseSnackbarMessage, detail: `File size exceeds the maximum allowed size of ${maxFileSizeMB}MB` });
    }
    return errorMessages;
  }

  getValidFileExtensions(): string[] {
    let validFileExtensions: string[] = [];
    if (this.fileReplaceMode && this.selectedAsset?.fileType) {
      validFileExtensions = [`.${this.selectedAsset.fileType}`];
    } else {
      if (this.selectedNode) {
        const category: AssetCategory = this.getTopmostParentData(this.selectedNode()!);
        validFileExtensions = category.validationRules.allowedExtensions;
      }
    }
    return validFileExtensions;
  }

  uploadFile(file: File): void {
    if (!this.selectedNode()) {
      return;
    }
    const category: AssetCategory = this.getTopmostParentData(this.selectedNode()!);
    const pathArr = this.selectedNode()!.data.path.split('/');
    if (pathArr <= 1) {
      return;
    }
    const productName = pathArr[1];
    const fileUploadRequest: FileUpload = {
      file: file,
      categoryId: category.id,
      productName: productName,
      repositoryKey: category.repositoryKey,
    };
    if (this.fileReplaceMode) {
      if (this.selectedAsset?.fileName) {
        fileUploadRequest.targetFileName = this.selectedAsset?.fileName;
      }
      this.handleFileReplacement(fileUploadRequest);
    } else {
      this.handleFileUpload(fileUploadRequest);
    }
  }

  handleFileUpload(fileUploadRequest: FileUpload): void {
    this.loading.set(true);
    from(this.oAuthService.getToken())
      .pipe(
        switchMap((token) => this.dataMaintenanceService.fileUploadOrReplace(fileUploadRequest, token)),
        takeUntilDestroyed(this.destroyed),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: () => {
          this.updateWorktreeRepository(this.activeWorktree(), fileUploadRequest.repositoryKey);
          this.snackbarService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'File uploaded successfully',
          });
          this.uncommitedUserAssets.set(0);
          this.loadUncommittedCount();
          this.loadAssets(this.selectedNode()!);
        },
        error: (err) => {
          console.error('Error uploading file', err);
        },
      });
  }

  handleFileReplacement(fileUploadRequest: FileUpload): void {
    this.loading.set(true);
    from(this.oAuthService.getToken())
      .pipe(
        switchMap((token) => this.dataMaintenanceService.fileUploadOrReplace(fileUploadRequest, token)),
        takeUntilDestroyed(this.destroyed),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: () => {
          this.updateWorktreeRepository(this.activeWorktree(), fileUploadRequest.repositoryKey);
          this.snackbarService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'File replaced successfully',
          });
          this.uncommitedUserAssets.set(0);
          this.loadUncommittedCount();
          this.loadAssets(this.selectedNode()!);
        },
        error: (err) => {
          console.error('Error replacing file', err);
        },
      });
  }

  getTopmostParentData<T>(node: TreeNode<T>): T | undefined {
    let current = node;

    // Traverse up to the topmost parent
    while (current.parent) {
      current = current.parent;
    }

    return current.data;
  }

  protected validateAndEnableWorkspaceOptions(): boolean {
    const node = this.selectedNode();
    return !this.selectedRepositoryCanAccess() || !node || this.activeWorktree() == '' || node.data.path.split('/').length <= 1;
  }

  protected validateAndEnableAddButton(): boolean {
    const node = this.selectedNode();
    return this.validateAndEnableWorkspaceOptions() || this.findValidationRulesForPath(node?.data?.path)?.allowUpload === false;
  }

  resolveOptions(options: MenuOption[], node: TreeNode | null = null): ResolvedMenuOption[] {
    return options.map((option) => ({
      ...option,
      disabled: option.enabledWhen ? !option.enabledWhen(node!) : false,
    }));
  }
}
